import { Flex, Text, TextInput, NumberInput, Select, Loader, Box, ActionIcon } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { IconPencil, IconX } from "@tabler/icons";
import { useEditableField } from "../../../../hooks/useEditableField";

interface EditableFieldRowProps {
  label: string;
  value: string;
  editable?: boolean;
  type?: 'text' | 'number' | 'date' | 'select';
  options?: string[];
  placeholder?: string;
  required?: boolean;
  min?: number;
  max?: number;
  fieldPath?: string;
  evidenceId?: string;
  isUpdating?: boolean;
  onSave?: (value: any) => Promise<void>;
}

const EditableFieldRow = ({
  label,
  value,
  editable = false,
  type = 'text',
  options = [],
  placeholder,
  required = false,
  min,
  max,
  isUpdating = false,
  onSave
}: EditableFieldRowProps) => {
  const {
    isEditing,
    isHovered,
    isSaving,
    error,
    editValue,
    displayValue,
    handleEdit,
    handleCancel,
    handleChange,
    handleSelectChange,
    handleDateChange,
    setIsHovered,
    inputRef,
  } = useEditableField({
    value,
    type,
    required,
    isUpdating,
    onSave,
    min,
    max,
  });

  const renderEditInput = () => {
    const commonProps = {
      value: editValue,
      placeholder: placeholder || label,
      error: error,
      size: "xs" as const,
      styles: {
        input: { fontSize: 12, height: 24 }
      }
    };

    switch (type) {
    case 'number':
      return (
        <NumberInput
          {...commonProps}
          onChange={handleChange}
          ref={inputRef as any}
        />
      );
    case 'date':
      return (
        <DatePicker
          {...commonProps}
          value={editValue ? new Date(editValue) : null}
          onChange={handleDateChange}
          // @ts-ignore
          format="MMM D, YYYY"
          dropdownType="popover"
          withinPortal
        />
      );
    case 'select':
      return (
        <Select
          {...commonProps}
          data={options}
          onChange={handleSelectChange}
          ref={inputRef as any}
        />
      );
    default:
      return (
        <TextInput
          {...commonProps}
          onChange={(e) => handleChange(e.target.value)}
          ref={inputRef}
        />
      );
    }
  };

  if (isEditing && editable) {
    return (
      <Flex h={32} align="center">
        <Flex align="center" gap={4} w={120}>
          <Text fz={12} h={16} sx={{ flex: 1 }}>
            {label}
          </Text>
          <ActionIcon 
            size="xs" 
            onClick={handleCancel}
            disabled={isSaving}
          >
            <IconX size={12} />
          </ActionIcon>
        </Flex>
        <Flex align="center" gap={4} sx={{ flex: 1 }}>
          <Box sx={{ flex: 1 }}>
            {renderEditInput()}
          </Box>
          {isSaving && <Loader size="xs" />}
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      h={32}
      align="center"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleEdit}
      sx={{
        cursor: editable && onSave ? 'pointer' : 'default',
        '&:hover': editable && onSave ? {
          backgroundColor: '#f8f9fa',
          borderRadius: 4
        } : {}
      }}
    >
      <Text fz={12} h={16} w={120}>
        {label}
      </Text>
      <Flex align="center" gap={4}>
        <Text fz={12}>
          {displayValue === null || displayValue === undefined || displayValue === '' 
            ? 'N/A' 
            : displayValue}
        </Text>
        {editable && onSave && isHovered && (
          <IconPencil size={14} color="#868e96" />
        )}
      </Flex>
    </Flex>
  );
};

export default EditableFieldRow;
